import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ERROR_STATUSES,
  ERROR_STATUS_LABELS,
  ErrorGroup,
  ErrorOccurrence,
  ErrorStatus,
  SystemAdminErrorsService,
  errorWhere,
} from '../../services/systemAdminErrorsService';
import { CopyableId, ErrorStatusBadge, LogsLink, formatWhen, orgLabel, timeAgo, useOrgNames } from './errorsCommon';

const OCCURRENCE_PAGE = 50;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="sys-field">
    <div className="sys-field__label">{label}</div>
    <div className="sys-field__value">{children}</div>
  </div>
);

/**
 * System Admin → Errors → one group: what the failure is (the fingerprint),
 * its history, triage (status + notes) and the latest occurrences. Each
 * occurrence opens its stored log line and a CloudWatch link for the request.
 */
const ErrorGroupDetail: React.FC = () => {
  const { groupId: groupIdParam } = useParams<{ groupId: string }>();
  const groupId = Number(groupIdParam);
  const navigate = useNavigate();
  const orgNames = useOrgNames();

  const [group, setGroup] = useState<ErrorGroup | null>(null);
  const [occurrences, setOccurrences] = useState<ErrorOccurrence[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemAdminErrorsService.fetchGroup(groupId, OCCURRENCE_PAGE, 0);
      setGroup(data.group);
      setNotes(data.group.notes ?? '');
      setOccurrences(data.occurrences);
      setHasMore(data.occurrences.length === OCCURRENCE_PAGE);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load this error.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { if (Number.isInteger(groupId) && groupId > 0) load(); }, [groupId, load]);

  const loadMore = async () => {
    try {
      const data = await SystemAdminErrorsService.fetchGroup(groupId, OCCURRENCE_PAGE, occurrences.length);
      setOccurrences(prev => [...prev, ...data.occurrences]);
      setHasMore(data.occurrences.length === OCCURRENCE_PAGE);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load more occurrences.');
    }
  };

  const save = async (change: { status?: ErrorStatus; notes?: string }) => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await SystemAdminErrorsService.updateGroup(groupId, change);
      setGroup(updated);
      if (change.notes !== undefined) setNotes(updated.notes ?? '');
      setSavedAt(Date.now());
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (!Number.isInteger(groupId) || groupId <= 0) return <Navigate to="/system-admin/errors" replace />;

  const notesDirty = group !== null && notes !== (group.notes ?? '');

  return (
    <div className="sys-page">
      <div className="sys-detail__head">
        {/* Back, not a fixed link: keeps the list's filters (they live in its URL). */}
        <Link to="/system-admin/errors" className="sys-back" title="Back to errors"
              onClick={e => { if (window.history.state?.idx > 0) { e.preventDefault(); navigate(-1); } }}>
          <i className="fas fa-arrow-left" aria-hidden="true" />Errors
        </Link>
        <h1 className="sys-detail__name"><code>{group?.event ?? `Error #${groupId}`}</code></h1>
        {group && <ErrorStatusBadge status={group.status} />}
      </div>

      <div className="sys-page__body">
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && !group ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading…</span></div>
          </div>
        ) : group && (
          <>
            <div className="sys-errors__cols mb-3">
              <div className="ivory-panel sys-panel sys-panel--pad">
                <h2 className="sys-panel__title">What failed</h2>
                <Field label="Event"><code>{group.event}</code></Field>
                <Field label="Where">{errorWhere(group)}</Field>
                <Field label="Error">
                  {group.errName || '—'}
                  {group.errCode && <span className="sys-tag">{group.errCode}</span>}
                  {group.errConstraint && <span className="sys-field__note">constraint {group.errConstraint}</span>}
                </Field>
                <Field label="Latest message"><span className="sys-errors__wrap">{group.sampleMessage || '—'}</span></Field>
              </div>

              <div className="ivory-panel sys-panel sys-panel--pad">
                <h2 className="sys-panel__title">History</h2>
                <Field label="First seen">{formatWhen(group.firstSeenAt)}</Field>
                <Field label="Last seen">
                  {formatWhen(group.lastSeenAt)}<span className="sys-field__note">{timeAgo(group.lastSeenAt)}</span>
                </Field>
                <Field label="Occurrences">{group.occurrenceCount.toLocaleString()}<span className="sys-field__note">all time</span></Field>
                <Field label="Reopened">{group.reopenedCount > 0 ? `${group.reopenedCount}×` : 'Never'}</Field>
                {group.resolvedAt && (
                  <Field label="Resolved">
                    {formatWhen(group.resolvedAt)}{group.resolvedBy && <span className="sys-field__note">by {group.resolvedBy}</span>}
                  </Field>
                )}
              </div>
            </div>

            <div className="ivory-panel sys-panel sys-panel--pad mb-3">
              <h2 className="sys-panel__title">Triage</h2>
              <div className="sys-period mb-2" role="group" aria-label="Status">
                {ERROR_STATUSES.map(s => (
                  <button key={s} type="button" className="sys-period__btn" aria-pressed={group.status === s}
                          disabled={saving || group.status === s} onClick={() => save({ status: s })}>
                    {ERROR_STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
              <p className="sys-panel__hint mb-2">
                A resolved error that happens again reopens as <strong>New</strong> and emails again. An ignored one stays ignored and never emails.
              </p>
              <label htmlFor="error-notes" className="small text-muted">Notes</label>
              <textarea id="error-notes" className="form-control form-control-sm" rows={3} maxLength={4000}
                        value={notes} onChange={e => setNotes(e.target.value)}
                        placeholder="What it is, what fixed it, a PR link… No child or staff details." />
              <div className="d-flex align-items-center gap-2 mt-2">
                <button type="button" className="btn btn-sm btn-success" disabled={saving || !notesDirty}
                        onClick={() => save({ notes })}>
                  Save notes
                </button>
                {notesDirty && (
                  <button type="button" className="btn btn-sm btn-link" disabled={saving}
                          onClick={() => setNotes(group.notes ?? '')}>Discard</button>
                )}
                {saveError && <span className="text-danger small">{saveError}</span>}
                {!saveError && savedAt && !notesDirty && <span className="sys-saved small">Saved</span>}
              </div>
            </div>

            <div className="ivory-panel sys-panel sys-panel--table">
              <div className="sys-panel__head px-3 pt-3">
                <h2 className="sys-panel__title">Occurrences</h2>
                <span className="sys-panel__hint">Newest first · kept for 30 days</span>
              </div>
              <div className="sys-table-scroll">
                <table className="sys-table sys-table--rows">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Organisation</th>
                      <th className="sys-table__num">User</th>
                      <th>Request</th>
                      <th>Reference</th>
                      <th>Logs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occurrences.map(o => {
                      const open = () => navigate(`/system-admin/errors/occurrences/${o.id}`);
                      return (
                        <tr key={o.id} tabIndex={0} onClick={open} onKeyDown={e => { if (e.key === 'Enter') open(); }}>
                          <td>{formatWhen(o.occurredAt)}</td>
                          <td>{orgLabel(orgNames, o.organisationId)}</td>
                          <td className="sys-table__num sys-table__muted">{o.activeUserId ?? o.userId ?? '—'}</td>
                          <td className="sys-table__muted">{errorWhere(o)}</td>
                          <td><CopyableId value={o.requestId} /></td>
                          <td><LogsLink href={o.logsLink} label="Logs" /></td>
                        </tr>
                      );
                    })}
                    {occurrences.length === 0 && (
                      <tr><td colSpan={6} className="sys-table__empty">No occurrences in the last 30 days (older ones are purged).</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {hasMore && (
              <div className="text-center mt-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={loadMore}>Load more</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ErrorGroupDetail;
